// Session bookkeeping for the portal's /cars list (a 5.8 MB whole-node read for
// staff roles) so the LIST views can skip their mount fetch when it is safe.
//
// "The Redux slice is non-empty" is NOT enough to skip:
//   * EDIT_CAR is a no-op reducer — after a portal add / update / delete /
//     approval the slice stays stale until the next fetchCars();
//   * the driver-detail Refresh (refreshUser withCars -> UPSERT_DRIVER_CARS)
//     can seed an EMPTY slice with ONE driver's cars, so cars != null does not
//     mean the full list was ever loaded;
//   * the store is not reset on sign-out, so the slice can belong to the
//     previous user of this tab (driver / fleetadmin slices are filtered).
//
// So a list view skips the fetch only when this module saw a full fetchCars()
// complete for the signed-in uid and no portal write to /cars happened since.
// Everything here is in-memory bookkeeping: no RTDB access of its own.
import { store } from "common";

let loadedForUid = null;
let generation = 0;
let inflight = null; // { uid, generation, promise }

// Evaluate ONCE per mount, in a const outside the effect (never inside an
// effect that lists the slice in its deps — FETCH_CARS replaces the slice
// object before the data lands, which looped ~1,360 full reads in prod).
export const carsFresh = (carlistdata, uid) =>
  !!(uid && loadedForUid === uid && carlistdata && carlistdata.cars);

// Call after any portal write to /cars: the next list-view mount refetches
// once (lazy — nothing is downloaded until someone actually opens a list).
export const markCarsStale = () => {
  generation += 1;
  loadedForUid = null;
};

// The store is not reset on sign-out, and AuthLoading fetches a driver's /
// fleetadmin's scoped cars straight into the slice. After A -> B -> A in one
// tab the slice can therefore hold B's cars while loadedForUid still says A.
// Any change of the signed-in uid (sign-out included) invalidates the session.
// In-memory only: a store subscription, no RTDB access. lastUid starts from
// the current state so importing this module never marks a load in flight stale.
const signedInUid = () => {
  const profile = store.getState().auth.profile;
  return (profile && profile.uid) || null;
};
let lastUid = signedInUid();
store.subscribe(() => {
  const uid = signedInUid();
  if (uid !== lastUid) {
    lastUid = uid;
    markCarsStale();
  }
});

// Full fetchCars() with the bookkeeping above. Two list views mounting while
// one download is still in flight share it instead of starting a second one.
export const loadCars = (dispatch, fetchCars, uid) => {
  if (inflight && inflight.uid === uid && inflight.generation === generation) {
    return inflight.promise;
  }
  const startedAt = generation;
  const promise = Promise.resolve(dispatch(fetchCars()))
    .then(() => {
      // A write during the download (generation moved) leaves it stale.
      if (startedAt === generation && store.getState().carlistdata.cars) {
        loadedForUid = uid;
      }
    })
    .catch(() => {})
    .then(() => {
      if (inflight && inflight.promise === promise) inflight = null;
    });
  inflight = { uid, generation: startedAt, promise };
  return promise;
};
